import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Light / dark background for the staff app and patient portal.
 * The public website and print views always stay light (print must be black on white).
 * The choice is a per-browser UI preference (localStorage), defaulting to the OS setting.
 */
export type Theme = 'light' | 'dark'
const KEY = 'lh:theme'
const THEMED = /^\/(app|portal|console)(\/|$)/

export function storedTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark') return v
  } catch {
    /* storage unavailable */
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(theme: Theme, pathname: string) {
  const dark = theme === 'dark' && THEMED.test(pathname)
  document.documentElement.classList.toggle('dark', dark)
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#08111f' : '#0f766e')
}

interface ThemeState {
  theme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
}
const Ctx = createContext<ThemeState | null>(null)

export function ThemeProvider({ children, subscribe }: { children: ReactNode; subscribe: (fn: (pathname: string) => void) => () => void }) {
  const [theme, setThemeState] = useState<Theme>(storedTheme)
  useEffect(() => {
    applyTheme(theme, window.location.pathname)
    return subscribe((path) => applyTheme(theme, path))
  }, [theme, subscribe])
  const setTheme = useCallback((t: Theme) => {
    try {
      localStorage.setItem(KEY, t)
    } catch {
      /* preference just won't persist */
    }
    setThemeState(t)
  }, [])
  const toggle = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme])
  return <Ctx.Provider value={{ theme, setTheme, toggle }}>{children}</Ctx.Provider>
}

export function useTheme() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useTheme must be used inside ThemeProvider')
  return c
}

/** True when the dark background is active (safe outside the provider: defaults to light). */
export function useIsDark() {
  return useContext(Ctx)?.theme === 'dark'
}
